import {
  Box,
  Text,
  VStack,
  HStack,
  Badge,
  Collapse,
  Button,
  useDisclosure,
  Heading,
  Flex,
  Icon,
} from "@chakra-ui/react";
import { ChevronDownIcon, ChevronRightIcon, InfoIcon } from "@chakra-ui/icons";
import { SuspectRowData } from "./table-data";
import type { SuspectingEvidence } from "../../../../../api/suspects";
import { useDownloadFile } from "../../../shared-logic/useDownloadFile";
import { METRIPORT_PRIMARY, METRIPORT_PRIMARY_HOVER_BG } from "../../button";

export function SuspectContent({ ...rowContents }: SuspectRowData) {
  return (
    <Box>
      <Heading mb="3">Suspect Condition</Heading>
      <SuspectDisplay tableRow={rowContents} />
    </Box>
  );
}

function SuspectDisplay({ tableRow }: { tableRow: SuspectRowData }) {
  return (
    <VStack spacing={6} align="stretch">
      <Box>
        <VStack spacing={2} align="stretch">
          <HStack justify="space-between" align="start">
            <Text fontSize="md" fontWeight="strong" color="gray.600" minW="120px">
              Condition:
            </Text>
            <Text fontSize="md" textAlign="right" flex={1}>
              {tableRow.condition}
            </Text>
          </HStack>
          <HStack justify="space-between" align="start">
            <Text fontSize="md" fontWeight="strong" color="gray.600" minW="120px">
              Code:
            </Text>
            <Text fontSize="md" textAlign="right" flex={1}>
              {tableRow.code}
            </Text>
          </HStack>
          <HStack justify="space-between" align="start">
            <Text fontSize="md" fontWeight="strong" color="gray.600" minW="120px">
              Group:
            </Text>
            <Text fontSize="md" textAlign="right" flex={1}>
              {tableRow.displayName}
            </Text>
          </HStack>
        </VStack>
      </Box>

      {/* Logic Tooltip Section */}
      {tableRow.logicTooltip && (
        <Box>
          <HStack spacing={2} align="start">
            <Icon as={InfoIcon} color="blue.500" mt={0.5} />
            <VStack align="start" spacing={1}>
              <Text fontSize="sm" fontWeight="medium" color="gray.700">
                Detection Logic:
              </Text>
              <Text fontSize="sm" color="gray.600">
                {tableRow.logicTooltip}
              </Text>
            </VStack>
          </HStack>
        </Box>
      )}

      <SuspectingEvidenceSection suspectingEvidence={tableRow.suspectingEvidence} />
    </VStack>
  );
}

function SuspectingEvidenceSection({
  suspectingEvidence,
}: {
  suspectingEvidence: SuspectingEvidence[];
}) {
  if (!suspectingEvidence || suspectingEvidence.length === 0) {
    return null;
  }

  return (
    <Box>
      <Text fontSize="lg" fontWeight="semibold" mb={3}>
        Evidence
      </Text>
      <VStack spacing={2} align="stretch">
        {suspectingEvidence
          .sort((a, b) => {
            // Sort by effectiveDateTime if available in FHIR data
            const aDate = a.fhir?.effectiveDateTime
              ? new Date(a.fhir.effectiveDateTime).getTime()
              : 0;
            const bDate = b.fhir?.effectiveDateTime
              ? new Date(b.fhir.effectiveDateTime).getTime()
              : 0;
            return bDate - aDate; // Most recent first
          })
          .map((evidence, index) => (
            <SuspectingEvidenceItem key={`${evidence.id}-${index}`} evidence={evidence} />
          ))}
      </VStack>
    </Box>
  );
}

function SuspectingEvidenceItem({ evidence }: { evidence: SuspectingEvidence }) {
  const { isOpen, onToggle } = useDisclosure();
  const { isDownloading, onDownloadFile } = useDownloadFile();

  evidence.resource_type =
    evidence.resource_type.charAt(0).toUpperCase() + evidence.resource_type.slice(1);

  // Check if we have fhir data in the evidence itself
  const hasFhirData = !!evidence.fhir;

  function getResourceDisplayName(): string {
    // Handle FHIR data in evidence itself
    if (hasFhirData && evidence.fhir) {
      const fhir = evidence.fhir as Record<string, unknown>;

      // Try different common display fields based on resource type
      const code = fhir.code as Record<string, unknown> | undefined;
      if (code?.coding && Array.isArray(code.coding) && code.coding[0]) {
        const coding = code.coding[0] as Record<string, unknown>;
        if (coding.display && typeof coding.display === "string") {
          const display = coding.display;
          return display.length > 50 ? display.substring(0, 50) + "..." : display;
        }
      }
      if (code?.text && typeof code.text === "string") {
        const text = code.text;
        return text.length > 50 ? text.substring(0, 50) + "..." : text;
      }
      if (fhir.name && typeof fhir.name === "string") return fhir.name;
      if (fhir.title && typeof fhir.title === "string") return fhir.title;
      if (fhir.description && typeof fhir.description === "string") return fhir.description;
      if (fhir.text && typeof fhir.text === "object" && fhir.text !== null) {
        const textObj = fhir.text as Record<string, unknown>;
        if (textObj.div && typeof textObj.div === "string") {
          // Extract text from HTML div
          const textContent = textObj.div.replace(/<[^>]*>/g, "").trim();
          return textContent.length > 30 ? textContent.substring(0, 30) + "..." : textContent;
        }
      }
    }

    return evidence.id;
  }

  const getResourceTypeColor = (resourceType: string): string => {
    const colorMap: Record<string, string> = {
      Observation: "blue",
      Condition: "green",
      Medication: "purple",
      Procedure: "orange",
      DiagnosticReport: "cyan",
      Encounter: "pink",
      Patient: "teal",
      Practitioner: "yellow",
      Organization: "red",
      Device: "gray",
      Specimen: "brown",
      Media: "indigo",
    };
    return colorMap[resourceType] || "gray";
  };

  return (
    <Box border="1px" borderColor="gray.200" borderRadius="md">
      <Button
        variant="ghost"
        onClick={onToggle}
        w="full"
        justifyContent="space-between"
        p={3}
        h="auto"
        _hover={{ bg: "gray.50" }}
      >
        <HStack spacing={3}>
          <VStack align="start" spacing={1}>
            <Badge
              colorScheme={getResourceTypeColor(evidence.resource_type)}
              variant="subtle"
              size="lg"
              fontSize="sm"
              px={3}
              py={1}
            >
              {evidence.resource_type}
            </Badge>
            <Text fontSize="sm" fontWeight="bold">
              {getResourceDisplayName()}
            </Text>
          </VStack>
        </HStack>
        {isOpen ? <ChevronDownIcon /> : <ChevronRightIcon />}
      </Button>

      <Collapse in={isOpen} animateOpacity>
        <Box p={3} bg="gray.50" borderTop="1px" borderColor="gray.200">
          {hasFhirData ? (
            <VStack spacing={4} align="stretch">
              {/* View Source button on the right side */}
              {evidence.data_source && (
                <Flex justifyContent="flex-end">
                  <Button
                    bg={METRIPORT_PRIMARY}
                    color="white"
                    _hover={{
                      bg: METRIPORT_PRIMARY_HOVER_BG,
                    }}
                    isLoading={isDownloading[evidence.data_source]?.downloading ?? false}
                    onClick={() => {
                      if (!evidence.data_source) return;
                      onDownloadFile({
                        docId: evidence.data_source,
                        docFilename: evidence.data_source,
                        conversionType: "html",
                      });
                    }}
                  >
                    View Source
                  </Button>
                </Flex>
              )}

              {/* Evidence content using fhir data */}
              <GenericFhirResourceDisplay
                fhirData={evidence.fhir as Record<string, unknown>}
                resourceType={evidence.resource_type}
              />
            </VStack>
          ) : (
            <Text fontSize="sm" color="gray.500">
              No FHIR data available
            </Text>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function GenericFhirResourceDisplay({
  fhirData,
  resourceType,
}: {
  fhirData: Record<string, unknown>;
  resourceType: string;
}) {
  if (!fhirData) {
    return (
      <Text fontSize="sm" color="gray.500">
        No FHIR data available
      </Text>
    );
  }

  // Use generic display for all resource types
  return <GenericResourceDetailsDisplay fhirData={fhirData} resourceType={resourceType} />;
}

function GenericResourceDetailsDisplay({
  fhirData,
  resourceType,
}: {
  fhirData: Record<string, unknown>;
  resourceType: string;
}) {
  const getResourceDetails = () => {
    const details: Array<{ label: string; value: string }> = [];

    // Basic resource info
    if (fhirData.resourceType && typeof fhirData.resourceType === "string") {
      details.push({ label: "Resource Type", value: fhirData.resourceType });
    }
    if (fhirData.id && typeof fhirData.id === "string") {
      details.push({ label: "ID", value: fhirData.id });
    }
    if (fhirData.status && typeof fhirData.status === "string") {
      details.push({ label: "Status", value: fhirData.status });
    }

    // Resource-specific fields
    const code = fhirData.code as Record<string, unknown> | undefined;
    if (code?.coding && Array.isArray(code.coding) && code.coding[0]) {
      const coding = code.coding[0] as Record<string, unknown>;
      if (coding.display && typeof coding.display === "string") {
        details.push({ label: "Code Display", value: coding.display });
      } else if (code.text && typeof code.text === "string") {
        details.push({ label: "Code Text", value: code.text });
      }
      if (coding.code && typeof coding.code === "string") {
        details.push({ label: "Code", value: coding.code });
      }
    } else if (code?.text && typeof code.text === "string") {
      details.push({ label: "Code Text", value: code.text });
    }
    if (fhirData.name && typeof fhirData.name === "string") {
      details.push({ label: "Name", value: fhirData.name });
    }
    if (fhirData.title && typeof fhirData.title === "string") {
      details.push({ label: "Title", value: fhirData.title });
    }
    if (fhirData.description && typeof fhirData.description === "string") {
      details.push({ label: "Description", value: fhirData.description });
    }

    // Value information (for observations and similar)
    const valueQuantity = fhirData.valueQuantity as Record<string, unknown> | undefined;
    if (valueQuantity?.value && typeof valueQuantity.value === "number") {
      const unit =
        valueQuantity.unit && typeof valueQuantity.unit === "string"
          ? ` ${valueQuantity.unit}`
          : "";
      details.push({
        label: "Value",
        value: `${valueQuantity.value}${unit}`,
      });
    }
    if (fhirData.valueString && typeof fhirData.valueString === "string") {
      details.push({ label: "Value", value: fhirData.valueString });
    }
    const valueCodeableConcept = fhirData.valueCodeableConcept as
      | Record<string, unknown>
      | undefined;
    if (valueCodeableConcept?.text && typeof valueCodeableConcept.text === "string") {
      details.push({ label: "Value", value: valueCodeableConcept.text });
    }

    // Date information
    const dateFields = [
      { key: "effectiveDateTime", label: "Effective Date" },
      { key: "performedDateTime", label: "Performed Date" },
      { key: "onsetDateTime", label: "Onset Date" },
      { key: "issued", label: "Issued Date" },
      { key: "date", label: "Date" },
    ];

    for (const field of dateFields) {
      const dateValue = fhirData[field.key];
      if (dateValue && typeof dateValue === "string") {
        try {
          const date = new Date(dateValue).toLocaleDateString();
          details.push({ label: field.label, value: date });
        } catch {
          details.push({ label: field.label, value: dateValue });
        }
      }
    }

    // Period information
    const effectivePeriod = fhirData.effectivePeriod as Record<string, unknown> | undefined;
    if (effectivePeriod?.start && typeof effectivePeriod.start === "string") {
      try {
        const startDate = new Date(effectivePeriod.start).toLocaleDateString();
        details.push({ label: "Effective Period Start", value: startDate });
      } catch {
        details.push({ label: "Effective Period Start", value: effectivePeriod.start });
      }
    }
    if (effectivePeriod?.end && typeof effectivePeriod.end === "string") {
      try {
        const endDate = new Date(effectivePeriod.end).toLocaleDateString();
        details.push({ label: "Effective Period End", value: endDate });
      } catch {
        details.push({ label: "Effective Period End", value: effectivePeriod.end });
      }
    }

    return details;
  };

  const details = getResourceDetails();

  return (
    <VStack spacing={4} align="stretch">
      <Text fontSize="lg" fontWeight="medium">
        {resourceType} Details:
      </Text>

      {/* Structured Details */}
      <Box bg="white" p={3} borderRadius="md" border="1px" borderColor="gray.200">
        <VStack spacing={2} align="stretch">
          {details.map((detail, index) => (
            <HStack key={index} justify="space-between" align="start">
              <Text fontSize="md" fontWeight="strong" color="gray.600" minW="120px">
                {detail.label}:
              </Text>
              <Text fontSize="md" textAlign="right" flex={1}>
                {detail.value}
              </Text>
            </HStack>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
}
